import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AlbumComment } from "@/app/components/album/comment";
import ImageHeader from "@/app/components/album/image-header";
import { AlbumInfo } from "@/app/components/album/info";
import { RecordLabelsInfo } from "@/app/components/album/record-labels";
import { AlbumFallback } from "@/app/components/fallbacks/album-fallbacks";
import { PreviewListFallback } from "@/app/components/fallbacks/home-fallbacks";
import { BadgesData } from "@/app/components/header-info";
import { MobilePageHeader } from "@/app/components/header/mobile-page-header";
import PreviewList from "@/app/components/home/preview-list";
import ListWrapper from "@/app/components/list-wrapper";
import { MobileSongList } from "@/app/components/mobile/mobile-media-list";
import { DataTable } from "@/app/components/ui/data-table";
import {
  useGetAlbum,
  useGetArtistAlbums,
  useGetGenreAlbums,
} from "@/app/hooks/use-album";
import { useHasHover } from "@/app/hooks/use-input-mode";
import { useIsMobile } from "@/app/hooks/use-mobile";
import { useIsOnline } from "@/store/cache.store";
import ErrorPage from "@/app/pages/error-page";
import { songsColumns } from "@/app/tables/songs-columns";
import { ROUTES } from "@/routes/routesList";
import { usePlayerActions } from "@/store/player.store";
import { ColumnFilter } from "@/types/columnFilter";
import { Albums } from "@/types/responses/album";
import { sortRecentAlbums } from "@/utils/album";
import { convertSecondsToHumanRead } from "@/utils/convertSecondsToTime";

export default function Album() {
  const { albumId } = useParams() as { albumId: string };
  const { setSongList } = usePlayerActions();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const hasHover = useHasHover();
  const isOnline = useIsOnline();
  const [accentColor, setAccentColor] = useState("");
  const columns = useMemo(
    () =>
      songsColumns({
        hasHover,
      }),
    [hasHover],
  );

  const {
    data: album,
    isLoading: albumIsLoading,
    isFetched,
    error: albumError,
  } = useGetAlbum(albumId);
  const { data: artist, isLoading: moreAlbumsIsLoading } = useGetArtistAlbums(
    album?.artistId || "",
  );
  const { data: randomAlbums, isLoading: randomAlbumsIsLoading } =
    useGetGenreAlbums(album?.genre || "");

  const moreAlbums = artist?.album;

  if (albumIsLoading) return <AlbumFallback />;
  if (isFetched && !album) {
    return (
      <ErrorPage
        status={albumError ? 500 : 404}
        statusText={t("error.notFound")}
      />
    );
  }
  if (!album) return <AlbumFallback />;

  const albumDuration = album.duration
    ? convertSecondsToHumanRead(album.duration)
    : null;

  const badges: BadgesData = [
    { content: album.year?.toString() ?? null, type: "text" },
    {
      content: album.genre ?? null,
      type: "link",
      link: ROUTES.ALBUMS.GENRE(album.genre),
    },
  ];

  const secondaryBadges: BadgesData = [
    {
      content: album.songCount
        ? t("playlist.songCount", { count: album.songCount })
        : null,
      type: "text",
    },
    {
      content: albumDuration
        ? t("playlist.duration", { duration: albumDuration })
        : null,
      type: "text",
    },
  ];

  const hasTrackNumbers = album.song.some((song) => song.track > 0);

  const columnsToShow: ColumnFilter[] = isMobile
    ? [
        ...(hasTrackNumbers ? ["trackNumber" as ColumnFilter] : []),
        "title",
        "duration",
        "select",
      ]
    : [
        ...(hasTrackNumbers ? ["trackNumber" as ColumnFilter] : []),
        "title",
        // 'artist',
        "duration",
        "playCount",
        "played",
        "bitRate",
        "contentType",
        "select",
      ];

  function removeCurrentAlbumFromList(moreAlbums: Albums[], sort = false) {
    if (moreAlbums.length === 0 || !album) return null;

    let list = moreAlbums.filter((item) => item.id !== album.id);

    if (sort) {
      list = sortRecentAlbums(list);
    }

    if (list.length > 16) list = list.slice(0, 16);

    if (list.length === 0) return null;

    return list;
  }

  const artistAlbums = moreAlbums
    ? removeCurrentAlbumFromList(moreAlbums, true)
    : null;

  const randomGenreAlbums =
    randomAlbums?.list && album.genre
      ? removeCurrentAlbumFromList(randomAlbums.list)
      : null;

  const albumComment = album.song.length > 0 ? album.song[0].comment : null;

  return (
    <div className="w-full">
      <MobilePageHeader
        variant="sub"
        title={album.name}
        accentColor={accentColor}
      />
      <ImageHeader
        type={t("album.headline")}
        title={album.name}
        subtitle={album.artist}
        artistId={album.artistId}
        artists={album.artists}
        coverArtId={album.coverArt}
        coverArtType="album"
        coverArtSize="700"
        coverArtAlt={album.name}
        badges={badges}
        secondaryBadges={secondaryBadges}
        onColorExtracted={setAccentColor}
      />

      <ListWrapper>
        <AlbumInfo album={album} />

        {album.songsUnavailable && !isOnline ? (
          <div className="rounded-md border border-border bg-muted/50 p-4 my-2">
            <h4 className="text-sm font-semibold">
              {t("album.detail.songsUnavailableTitle")}
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              {t("album.detail.songsUnavailable")}
            </p>
          </div>
        ) : isMobile ? (
          <MobileSongList
            songs={album.song}
            onPlaySong={(index) =>
              setSongList(
                album.song,
                index,
                false,
                { albumId: album.id },
                album.name,
              )
            }
            getIndexLabel={(song, index) =>
              hasTrackNumbers && song.track > 0 ? song.track : index + 1
            }
            showDiscNumber={true}
          />
        ) : (
          <DataTable
            columns={columns}
            data={album.song}
            handlePlaySong={(row) =>
              setSongList(
                album.song,
                row.index,
                false,
                { albumId: album.id },
                album.name,
              )
            }
            columnFilter={columnsToShow}
            showDiscNumber={true}
            variant="modern"
          />
        )}

        {albumComment && <AlbumComment comment={albumComment} />}

        <RecordLabelsInfo album={album} />

        <div className="mt-4">
          {moreAlbumsIsLoading && <PreviewListFallback />}
          {artistAlbums && !moreAlbumsIsLoading && album.artistId && (
            <PreviewList
              list={artistAlbums}
              showMore={true}
              title={t("album.more.listTitle")}
              moreTitle={t("album.more.discography")}
              moreRoute={ROUTES.ALBUMS.ARTIST(album.artistId, album.artist)}
            />
          )}

          {randomAlbumsIsLoading && <PreviewListFallback />}
          {!randomAlbumsIsLoading && randomGenreAlbums && (
            <PreviewList
              list={randomGenreAlbums}
              moreRoute={ROUTES.ALBUMS.GENRE(album.genre)}
              title={t("album.more.genreTitle", {
                genre: album.genre,
              })}
            />
          )}
        </div>
      </ListWrapper>
    </div>
  );
}
